import { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Users,
  School,
  GraduationCap,
  DollarSign,
  TrendingUp,
  Calendar,
  CreditCard,
  CheckCircle,
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { dashboardApi, DashboardStats } from '../../services/schoolApi';

const Dashboard = () => {
  const { language } = useLanguage();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [schoolYear, setSchoolYear] = useState('');

  const schoolYears = (() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 5 }, (_, i) => {
      const y = currentYear - i;
      return `${y}-${y + 1}`;
    });
  })();

  const currentText = language === 'fr' ? {
    title: 'Tableau de bord',
    subtitle: 'Vue d\'ensemble de votre école',
    overview: 'Vue d\'ensemble',
    students: 'Élèves',
    classrooms: 'Classes',
    teachers: 'Enseignants',
    schoolFees: 'Frais scolaires',
    paymentsThisMonth: 'Paiements ce mois',
    collectionRate: 'Taux de recouvrement',
    totalAmount: 'Montant total',
    recentPayments: 'Paiements récents',
    studentName: 'Élève',
    fee: 'Frais',
    amount: 'Montant',
    date: 'Date',
    method: 'Méthode',
    status: 'Statut',
    topClasses: 'Classes principales',
    className: 'Classe',
    level: 'Niveau',
    studentCount: 'Nombre d\'élèves',
    byGender: 'Par genre',
    male: 'Garçons',
    female: 'Filles',
    byLevel: 'Par niveau',
    byPeriodicity: 'Par périodicité',
    unique: 'Unique',
    mensuel: 'Mensuel',
    trimestriel: 'Trimestriel',
    paymentMethods: 'Méthodes de paiement',
    cash: 'Espèces',
    mobile_money: 'Mobile Money',
    bank_transfer: 'Virement bancaire',
    check: 'Chèque',
    financial: 'Situation financière',
    expected: 'Attendu',
    collected: 'Collecté',
    outstanding: 'Impayé',
    period: 'Période',
    completed: 'Complété',
    partial: 'Partiel',
    pending: 'En attente',
    schoolYear: 'Année scolaire',
    allYears: 'Toutes les années',
    annuel: 'Annuel',
  } : {
    title: 'Dashboard',
    subtitle: 'Overview of your school',
    overview: 'Overview',
    students: 'Students',
    classrooms: 'Classrooms',
    teachers: 'Teachers',
    schoolFees: 'School Fees',
    paymentsThisMonth: 'Payments this month',
    collectionRate: 'Collection rate',
    totalAmount: 'Total amount',
    recentPayments: 'Recent payments',
    studentName: 'Student',
    fee: 'Fee',
    amount: 'Amount',
    date: 'Date',
    method: 'Method',
    status: 'Status',
    topClasses: 'Top classes',
    className: 'Class',
    level: 'Level',
    studentCount: 'Student count',
    byGender: 'By gender',
    male: 'Boys',
    female: 'Girls',
    byLevel: 'By level',
    byPeriodicity: 'By periodicity',
    unique: 'One-time',
    mensuel: 'Monthly',
    trimestriel: 'Quarterly',
    paymentMethods: 'Payment methods',
    cash: 'Cash',
    mobile_money: 'Mobile Money',
    bank_transfer: 'Bank transfer',
    check: 'Check',
    financial: 'Financial situation',
    expected: 'Expected',
    collected: 'Collected',
    outstanding: 'Outstanding',
    period: 'Period',
    completed: 'Completed',
    partial: 'Partial',
    pending: 'Pending',
    schoolYear: 'School Year',
    allYears: 'All years',
    annuel: 'Annual',
  };

  useEffect(() => {
    fetchDashboard();
  }, [schoolYear]);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const data = await dashboardApi.getSchoolStats(schoolYear || undefined);
      setStats(data);
      setError('');
    } catch (err: any) {
      console.error('Dashboard error:', err);
      setError(err.message || 'Erreur lors du chargement du tableau de bord');
    } finally {
      setLoading(false);
    }
  };

  const getPaymentMethodLabel = (method: string) => {
    switch (method) {
      case 'cash': return currentText.cash;
      case 'mobile_money': return currentText.mobile_money;
      case 'bank_transfer': return currentText.bank_transfer;
      case 'check': return currentText.check;
      default: return method;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'partial': return 'warning';
      case 'pending': return 'default';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed': return currentText.completed;
      case 'partial': return currentText.partial;
      case 'pending': return currentText.pending;
      default: return status;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!stats) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="info">
          Aucune donnée de statistiques disponible. Veuillez vérifier que l'API fonctionne correctement.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#0D3C4C', mb: 1 }}>
            {currentText.title}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {currentText.subtitle}
          </Typography>
          {stats.period && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {currentText.period}: {stats.period.month} - {stats.period.schoolYear}
            </Typography>
          )}
        </Box>
        <FormControl sx={{ minWidth: 180 }} size="small">
          <InputLabel>{currentText.schoolYear}</InputLabel>
          <Select
            value={schoolYear}
            onChange={(e) => setSchoolYear(e.target.value)}
            label={currentText.schoolYear}
          >
            <MenuItem value="">{currentText.allYears}</MenuItem>
            {schoolYears.map((y) => (
              <MenuItem key={y} value={y}>{y}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Grid container spacing={3}>
        {/* Cartes de statistiques principales */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', background: '#0D3C4C' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Users size={40} color="white" />
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>
                    {stats?.overview?.totalStudents || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                    {currentText.students}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', background: '#1A5F7A' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <School size={40} color="white" />
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>
                    {stats?.overview?.totalClassrooms || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                    {currentText.classrooms}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', background: '#2E8B9E' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <GraduationCap size={40} color="white" />
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>
                    {stats?.overview?.totalTeachers || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                    {currentText.teachers}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', background: '#159895' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <DollarSign size={40} color="white" />
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>
                    {stats?.overview?.totalSchoolFees || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                    {currentText.schoolFees}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Paiements ce mois */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Calendar size={24} color="#0D3C4C" />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#0D3C4C' }}>
                  {currentText.paymentsThisMonth}
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700, color: '#0D3C4C', mb: 1 }}>
                {stats?.payments?.thisMonth?.count || 0}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {currentText.totalAmount}: {(stats?.payments?.thisMonth?.totalAmount || 0).toLocaleString()} CDF
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Taux de recouvrement */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <TrendingUp size={24} color="#0D3C4C" />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#0D3C4C' }}>
                  {currentText.collectionRate}
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700, color: '#0D3C4C', mb: 1 }}>
                {stats?.overview?.collectionRate || 0}%
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {currentText.collected}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {(stats?.payments?.financial?.totalCollected || 0).toLocaleString()}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {currentText.outstanding}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#d32f2f' }}>
                    {(stats?.payments?.financial?.outstanding || 0).toLocaleString()}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Méthodes de paiement */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CreditCard size={24} color="#0D3C4C" />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#0D3C4C' }}>
                  {currentText.paymentMethods}
                </Typography>
              </Box>
              {(stats?.payments?.thisMonth?.byMethod || []).map((method) => (
                <Box key={method.method} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {getPaymentMethodLabel(method.method)}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {method.count} ({method.total.toLocaleString()})
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        {/* Classes principales */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#0D3C4C', mb: 2 }}>
                {currentText.byGender}
              </Typography>
              {(stats?.students?.byGender || []).map((g) => {
                const total = stats?.students?.total || 1;
                const pct = Math.round((g.count / total) * 100);
                const label = g.gender === 'M' ? currentText.male : currentText.female;
                return (
                  <Box key={g.gender} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2">{label}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{g.count} ({pct}%)</Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={pct}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: '#e0e0e0',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: g.gender === 'M' ? '#0D3C4C' : '#E91E63',
                          borderRadius: 4,
                        },
                      }}
                    />
                  </Box>
                );
              })}
            </CardContent>
          </Card>
        </Grid>

        {/* Frais par périodicité */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#0D3C4C', mb: 2 }}>
                {currentText.byPeriodicity}
              </Typography>
              {(stats?.schoolFees?.byPeriodicity || []).map((p) => {
                const label =
                  p.periodicity === 'unique' ? currentText.unique
                  : p.periodicity === 'mensuel' ? currentText.mensuel
                  : p.periodicity === 'trimestriel' ? currentText.trimestriel
                  : p.periodicity === 'annuel' ? currentText.annuel
                  : p.periodicity;
                return (
                  <Box key={p.periodicity} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, alignItems: 'center' }}>
                    <Chip label={label} size="small" variant="outlined" />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.count}</Typography>
                  </Box>
                );
              })}
            </CardContent>
          </Card>
        </Grid>

        {/* Situation financière */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#0D3C4C', mb: 2 }}>
                {currentText.financial}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">{currentText.expected}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {(stats?.payments?.financial?.totalExpected || 0).toLocaleString()} CDF
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">{currentText.collected}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#4caf50' }}>
                    {(stats?.payments?.financial?.totalCollected || 0).toLocaleString()} CDF
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">{currentText.outstanding}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#d32f2f' }}>
                    {(stats?.payments?.financial?.outstanding || 0).toLocaleString()} CDF
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={stats?.payments?.financial?.collectionRate || 0}
                  sx={{
                    height: 10,
                    borderRadius: 5,
                    mt: 1,
                    backgroundColor: '#ffcdd2',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: '#4caf50',
                      borderRadius: 5,
                    },
                  }}
                />
                <Typography variant="caption" align="center" color="text.secondary">
                  {currentText.collectionRate}: {stats?.payments?.financial?.collectionRate || 0}%
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#0D3C4C', mb: 2 }}>
                {currentText.topClasses}
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{currentText.className}</TableCell>
                      <TableCell>{currentText.level}</TableCell>
                      <TableCell align="right">{currentText.studentCount}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(stats?.classrooms?.topClassrooms || []).map((classroom) => (
                      <TableRow key={classroom.code}>
                        <TableCell sx={{ fontWeight: 500 }}>{classroom.name}</TableCell>
                        <TableCell>
                          <Chip label={classroom.level} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {classroom.studentCount}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Paiements récents */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#0D3C4C', mb: 2 }}>
                {currentText.recentPayments}
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{currentText.studentName}</TableCell>
                      <TableCell>{currentText.amount}</TableCell>
                      <TableCell>{currentText.status}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(stats?.payments?.recent || [])
                      .filter(payment => payment.studentId !== null)
                      .map((payment) => (
                      <TableRow key={payment._id}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {payment.studentId?.firstName} {payment.studentId?.lastName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {payment.studentId?.matricule}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {payment.amount.toLocaleString()} {payment.schoolFeeId?.currency}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {payment.schoolFeeId?.label}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={getStatusLabel(payment.status)} 
                            size="small" 
                            color={getStatusColor(payment.status) as any}
                            icon={payment.status === 'completed' ? <CheckCircle size={16} /> : undefined}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Dashboard;
